/**
 * Versioned browser cookie for multi-practitioner calendar selection.
 * Stores practitioner IDs only — never names, patients, or appointment details.
 */

export const CALENDAR_SELECTION_COOKIE_NAME = 'unify_calendar_selection_v1';
export const CALENDAR_SELECTION_SCHEMA_VERSION = 1;
/** ~180 days */
const MAX_AGE_SECONDS = 180 * 24 * 60 * 60;
/** Conservative cookie payload size (bytes) before falling back to sentinel / trim. */
export const CALENDAR_SELECTION_MAX_COOKIE_BYTES = 3500;
/** Hard cap on explicit IDs when not using the all-accessible sentinel. */
export const CALENDAR_SELECTION_MAX_IDS = 40;

export type CalendarSelectionCookiePayload = {
  v: number;
  ids: string[];
  /** When true, UI restores all currently accessible practitioner calendars. */
  all?: boolean;
};

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const prefix = `${name}=`;
  for (const row of document.cookie.split(';')) {
    const trimmed = row.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return undefined;
}

function writeCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  const secure =
    typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}

function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/** Defensive parse — returns null for missing/malformed/unsupported versions. */
export function parseCalendarSelectionCookie(raw: string | undefined): CalendarSelectionCookiePayload | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const obj = parsed as Record<string, unknown>;
    if (obj.v !== CALENDAR_SELECTION_SCHEMA_VERSION) return null;
    if (!Array.isArray(obj.ids)) return null;
    const ids = obj.ids.filter(isNonEmptyString).map((id) => id.trim());
    const all = obj.all === true;
    return { v: CALENDAR_SELECTION_SCHEMA_VERSION, ids, ...(all ? { all: true } : {}) };
  } catch {
    return null;
  }
}

export function readCalendarSelectionCookie(): CalendarSelectionCookiePayload | null {
  return parseCalendarSelectionCookie(readCookie(CALENDAR_SELECTION_COOKIE_NAME));
}

export function writeCalendarSelectionCookie(payload: CalendarSelectionCookiePayload): void {
  const normalized: CalendarSelectionCookiePayload = {
    v: CALENDAR_SELECTION_SCHEMA_VERSION,
    ids: payload.all ? [] : [...new Set(payload.ids.filter(isNonEmptyString))].slice(0, CALENDAR_SELECTION_MAX_IDS),
    ...(payload.all ? { all: true as const } : {}),
  };
  const encoded = JSON.stringify(normalized);
  if (encoded.length > CALENDAR_SELECTION_MAX_COOKIE_BYTES && !normalized.all) {
    writeCookie(
      CALENDAR_SELECTION_COOKIE_NAME,
      JSON.stringify({ v: CALENDAR_SELECTION_SCHEMA_VERSION, ids: [], all: true }),
    );
    return;
  }
  writeCookie(CALENDAR_SELECTION_COOKIE_NAME, encoded);
}

export function clearCalendarSelectionCookie(): void {
  deleteCookie(CALENDAR_SELECTION_COOKIE_NAME);
}

export interface SanitizeCalendarSelectionArgs {
  saved: CalendarSelectionCookiePayload | null;
  accessibleIds: readonly string[];
  currentPractitionerId: string;
}

/**
 * Intersect cookie with current access. Never returns empty — falls back to self.
 * Rewrites the cookie when sanitization changes the value.
 */
export function sanitizeCalendarSelection(args: SanitizeCalendarSelectionArgs): string[] {
  const accessible = new Set(args.accessibleIds);
  const selfId = args.currentPractitionerId;

  let ordered: string[];
  if (args.saved?.all) {
    ordered = args.accessibleIds.filter((id) => accessible.has(id));
  } else if (args.saved?.ids?.length) {
    ordered = args.saved.ids.filter((id) => accessible.has(id));
  } else {
    ordered = accessible.has(selfId) ? [selfId] : args.accessibleIds.slice(0, 1);
  }

  if (ordered.length === 0) {
    ordered = accessible.has(selfId) ? [selfId] : args.accessibleIds.slice(0, 1);
  }
  if (ordered.length === 0 && selfId) {
    ordered = [selfId];
  }

  const nextPayload: CalendarSelectionCookiePayload = args.saved?.all
    ? { v: CALENDAR_SELECTION_SCHEMA_VERSION, ids: [], all: true }
    : { v: CALENDAR_SELECTION_SCHEMA_VERSION, ids: ordered };

  const prev = args.saved;
  const changed =
    !prev ||
    prev.all !== nextPayload.all ||
    prev.ids.join('|') !== (nextPayload.all ? '' : ordered.join('|'));

  if (changed) {
    writeCalendarSelectionCookie(nextPayload);
  }

  return ordered;
}
